/**
 * ThemeTunerDialog.jsx — Appearance.
 *
 * One floating, draggable panel (no scrim: the point is watching the app
 * recolour behind it) with two pages:
 *
 *   APPEARANCE — the themes as small pictures, grouped Light / Dark / Yours;
 *   the one in use is ringed. Under them, WHERE IT APPLIES: the three areas
 *   of the app (Home & projects, Workspace, Data pages), each following the
 *   theme above unless given one of its own. Then the way into Customise.
 *
 *   CUSTOMISE — the app in miniature, repainting as each colour of the
 *   current theme is tuned; Reset drops the overrides.
 *
 * Picking a theme applies at once and saves to the account; nothing here
 * needs a Save.
 */
import { useState, useEffect, useRef } from 'react';
import './ThemeTunerDialog.css';
import ColorPicker from '../../shared/ColorPicker';
import ThemePreview from './ThemePreview';
import {
  TUNABLE_TOKENS, THEME_META,
  setThemeOverride, resetThemeOverrides, getThemeOverrides, duplicateToCustom, getActiveTheme, DEFAULT_THEME,
} from '../../../utils/theme';

function readVar(name) {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return toHex6(raw);
  } catch {
    return '#000000';
  }
}
function toHex6(v) {
  if (!v) return '#000000';
  let s = v.trim();
  if (s[0] === '#') {
    if (s.length === 4) s = '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
    return s.slice(0, 7);
  }
  return s;
}
function hintFor(token) {
  for (const g of TUNABLE_TOKENS) {
    for (const [name, label, hint] of g.tokens) {
      if (name === token) return hint ? `${label} — ${hint}` : label;
    }
  }
  return null;
}
function seedValues() {
  const v = {};
  TUNABLE_TOKENS.forEach(g => g.tokens.forEach(([name]) => { v[name] = readVar(name); }));
  return v;
}

// The themes, grouped the way people think of them. 'test' is a working
// theme for the developer and shows only while it is the one in use.
const GROUPS = [
  { id: 'light', title: 'Light', ids: ['auto', 'light', 'light-band', 'sepia'] },
  { id: 'dark', title: 'Dark', ids: ['dark', 'dark-band', 'graphite', 'grey', 'slate', 'nord', 'midnight', 'amoled', 'high-contrast', 'test'] },
  { id: 'mine', title: 'Yours', ids: ['custom'] },
];
const labelOf = (id) => THEME_META.find(t => t.id === id)?.label || (id ? id.charAt(0).toUpperCase() + id.slice(1) : '');

/* A theme as a small picture: its page, a panel on it, a line of accent,
   two lines of "text". Auto is split light / dark down the middle. */
function ThemeCard({ id, label, preview, active, onPick }) {
  const [app, panel, accent] = preview;
  const split = id === 'auto';
  return (
    <button
      type="button"
      className={`tt-card${active ? ' is-active' : ''}`}
      onClick={onPick}
      aria-pressed={active}
      title={label}
    >
      <span className="tt-card-pic" style={{ background: split ? `linear-gradient(100deg, ${app} 50%, ${panel} 50%)` : app }}>
        <span className="tt-card-panel" style={{ background: split ? '#ffffff' : panel, borderColor: split ? '#d9dde4' : 'transparent' }}>
          <i style={{ background: accent }} />
          <i style={{ background: split ? '#c9ced8' : 'color-mix(in srgb, ' + panel + ' 70%, ' + (isDark(app) ? '#fff' : '#000') + ')' }} />
          <i style={{ background: split ? '#e1e5ec' : 'color-mix(in srgb, ' + panel + ' 82%, ' + (isDark(app) ? '#fff' : '#000') + ')' }} />
        </span>
        {split && (
          <span className="tt-card-panel is-right" style={{ background: '#1a1a1a', borderColor: 'transparent' }}>
            <i style={{ background: accent }} />
            <i style={{ background: '#5a5a5a' }} />
            <i style={{ background: '#3a3a3a' }} />
          </span>
        )}
      </span>
      <span className="tt-card-name">{label}</span>
      {active && <span className="tt-card-tick" aria-hidden="true">✓</span>}
    </button>
  );
}
function isDark(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return true;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 140;
}

export default function ThemeTunerDialog({ theme = DEFAULT_THEME, onThemeChange, onClose }) {
  const [values, setValues] = useState(seedValues);
  const [customising, setCustomising] = useState(false);
  const [hoverToken, setHoverToken] = useState(null);

  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setValues(seedValues()));
    return () => cancelAnimationFrame(id);
  }, [theme]);
  useEffect(() => {
    const onChanged = () => setValues(seedValues());
    window.addEventListener('pidly:theme-changed', onChanged);
    return () => window.removeEventListener('pidly:theme-changed', onChanged);
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const startDrag = (e) => {
    if (e.button !== 0 || e.target.closest('button, select, input')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const onMove = (ev) => {
      const x = Math.max(4, Math.min(window.innerWidth - 80, ev.clientX - offX));
      const y = Math.max(4, Math.min(window.innerHeight - 40, ev.clientY - offY));
      setPos({ x, y });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  };

  const editTheme = theme === 'auto' ? getActiveTheme() : theme;
  const editLabel = labelOf(editTheme);

  const change = (name, hex) => {
    setValues(prev => ({ ...prev, [name]: hex }));
    setThemeOverride(editTheme, name, hex);
  };
  const reset = () => {
    resetThemeOverrides(editTheme);
    setValues(seedValues());
  };
  const hasOverrides = Object.keys(getThemeOverrides(editTheme)).length > 0;

  const customOv = getThemeOverrides('custom');
  const previewOf = (t) => (t.id === 'custom'
    ? [customOv['--surface-app'] || t.preview[0], customOv['--surface-panel'] || t.preview[1], customOv['--accent'] || t.preview[2]]
    : t.preview);

  const duplicateToMine = () => {
    duplicateToCustom();
    setCustomising(true);
    onThemeChange?.('custom');
  };


  return (
    <div
      ref={panelRef}
      className="theme-tuner tt"
      role="dialog"
      aria-label="Appearance"
      style={pos ? { left: pos.x, top: pos.y, right: 'auto', transform: 'none' } : undefined}
    >
      <div className="tt-head" onMouseDown={startDrag}>
        {customising ? (
          <>
            <button type="button" className="tt-back" onClick={() => setCustomising(false)} aria-label="Back to Appearance" title="Back">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div className="tt-head-text">
              <h3>Customise {editLabel}</h3>
              <p>Each colour applies as you pick it and saves to your account.</p>
            </div>
          </>
        ) : (
          <div className="tt-head-text">
            <h3>Appearance</h3>
            <p>Pick a theme. It applies everywhere, unless an area below has one of its own.</p>
          </div>
        )}
        <button type="button" className="tt-x" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="tt-body">
        {customising ? (
          <div className="tt-customise">
            <div className="tt-preview">
              <ThemePreview hoverToken={hoverToken} onHoverToken={setHoverToken} />
              <p className="tt-preview-hint">
                {hoverToken ? (hintFor(hoverToken) || 'Where this colour lands.') : 'Point at a colour below to see where it lands.'}
              </p>
            </div>
            {TUNABLE_TOKENS.map(group => (
              <div key={group.group} className="tt-group">
                <div className="tt-group-title">{group.group}</div>
                <div className="tt-grid">
                  {group.tokens.map(([name, label, hint]) => (
                    <div
                      key={name}
                      className={`tt-row${hoverToken === name ? ' is-lit' : ''}`}
                      title={name}
                      onMouseEnter={() => setHoverToken(name)}
                      onMouseLeave={() => setHoverToken(null)}
                    >
                      <ColorPicker value={values[name] || '#000000'} onChange={(hex) => change(name, hex)} title={label} size={18} />
                      <span className="tt-row-text">
                        <span className="tt-row-label">{label}</span>
                        {hint && <span className="tt-row-hint">{hint}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {GROUPS.map(g => {
              const list = THEME_META.filter(t => g.ids.includes(t.id) && (t.id !== 'test' || theme === 'test'));
              if (!list.length) return null;
              return (
                <section key={g.id} className="tt-section">
                  <h4 className="tt-section-title">{g.title}</h4>
                  <div className="tt-cards">
                    {list.map(t => (
                      <ThemeCard
                        key={t.id}
                        id={t.id}
                        label={t.label}
                        preview={previewOf(t)}
                        active={theme === t.id}
                        onPick={() => { if (theme !== t.id) onThemeChange?.(t.id); }}
                      />
                    ))}
                    {g.id === 'mine' && theme !== 'custom' && (
                      <button type="button" className="tt-card tt-card-make" onClick={duplicateToMine} title={`Start from ${editLabel} and change any colour`}>
                        <span className="tt-card-pic is-make" aria-hidden="true">+</span>
                        <span className="tt-card-name">Make my own from {editLabel}</span>
                      </button>
                    )}
                  </div>
                  {g.id === 'light' && theme === 'auto' && (
                    <p className="tt-note">Auto follows your system: right now that is <b>{editLabel}</b>.</p>
                  )}
                </section>
              );
            })}

            <section className="tt-section">
              <button type="button" className="tt-customise-btn" onClick={() => { setHoverToken(null); setCustomising(true); }}>
                <span>
                  <b>Customise the colours of {editLabel}</b>
                  <small>{theme === 'custom' ? 'Your own theme: every colour is yours to set.' : 'Tune any colour of this theme. Reset brings it back.'}</small>
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </section>
          </>
        )}
      </div>

      <div className="tt-foot">
        {customising ? (
          <button type="button" className="tt-btn" onClick={reset} disabled={!hasOverrides}>Reset {editLabel} colours</button>
        ) : <span className="tt-foot-note">Changes apply straight away.</span>}
        <button type="button" className="tt-btn is-primary" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}
